import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import SelectCertification from "../../components/SelectCertification";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { UserCertification } from "../../types/certification";
import {
  useAddUserCertificationMutation,
  useUpdateUserCertificationMutation,
} from "../../services/talent.service";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { BaseModal, Button, StyledTextField } from "../../components/global";

interface ModalUserCertificationFormProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  data: UserCertification | null;
}

const ModalUserCertificationForm = ({
  open,
  onClose,
  userId,
  data,
}: ModalUserCertificationFormProps) => {
  const [add, { isLoading: adding }] = useAddUserCertificationMutation();
  const [edit, { isLoading: editing }] = useUpdateUserCertificationMutation();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      payload.user_id = userId;

      if (!!payload.issue_date)
        payload.issue_date = dayjs(payload.issue_date).format("YYYY-MM-DD");
      if (!!payload.expiration_date)
        payload.expiration_date = dayjs(payload.expiration_date).format(
          "YYYY-MM-DD"
        );

      if (!data) {
        await add(payload).unwrap();
        toast.success("Certification added");
      } else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
        toast.success("Certification updated");
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("certification", data?.certification_id || 0);
      setValue("certification_number", data?.certification_number || "");
      setValue("certification_url", data?.certification_url || "");
      setValue("issue_date", data?.issue_date ? dayjs(data.issue_date) : null);
      setValue("expiration_date", data?.expiration_date ? dayjs(data.expiration_date) : null);
    }
  }, [data, open, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} User Certification`}
      maxWidth="sm"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Add" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="certification"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <SelectCertification
                  onChange={onChange}
                  value={value || 0}
                  error={!!errors.certification}
                  helperText={
                    !!errors.certification && "This field is required"
                  }
                />
              )}
            />
            <Controller
              name="certification_number"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Certification Number (optional)"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                />
              )}
            />
            <Controller
              name="certification_url"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Certification URL (optional)"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                />
              )}
            />
            <Stack direction="row" spacing={2}>
            <Controller
              name="issue_date"
              control={control}
              rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                <DesktopDatePicker
                  label="Issue Date"
                  inputFormat="MMM DD, YYYY"
                  value={value || null}
                  showToolbar={true}
                  onChange={onChange}
                  renderInput={(params) => (
                      <StyledTextField
                      {...params}
                        fullWidth
                      error={!!errors.issue_date}
                      helperText={
                        !!errors.issue_date && "This field is required"
                      }
                    />
                  )}
                />
              )}
            />
            <Controller
              name="expiration_date"
              control={control}
                render={({ field: { onChange, value } }) => (
                <DesktopDatePicker
                  label="Expiration Date"
                  inputFormat="MMM DD, YYYY"
                  value={value || null}
                  showToolbar={true}
                  onChange={onChange}
                  renderInput={(params) => (
                      <StyledTextField {...params} fullWidth />
                  )}
                />
              )}
            />
            </Stack>
          </Stack>
          </Box>
      </form>
    </BaseModal>
  );
};
export default ModalUserCertificationForm;
